import { Button } from '@pmi/dsm-react';
import PlayIcon from '@pmi/dsm-react/dist/components/icons/16/VideoIcon';
import { useEffect, useMemo, useState } from 'react';
import EmbedVideo from '../EmbedVideo/EmbedVideo';
import MarkdownViewer from '../MarkdownViewer';
import styles from './hubWelcomeContent.module.scss';
import EmbedVideoModal from '../EmbedVideoModal/EmbedVideoModal';
import {
  defaultWelcomeContent,
  defaultWelcomeTitle,
} from '../../utils/defaultTexts';

interface HubWelcomeContentProps {
  videoUrl: string;
  content: string;
  title: string;
  logoUrl: string;
  transcript: string;
  adminInfo?: { name: string; email: string };
}

export default function HubWelcomeContent({
  content,
  videoUrl,
  title,
  logoUrl,
  transcript,
  adminInfo = null,
}: HubWelcomeContentProps) {
  const [showModal, setShowModal] = useState<boolean>(false);
  const welcomeMessageText = useMemo(() => {
    let message = content || defaultWelcomeContent;
    let replacement = '';
    if (adminInfo) {
      const name = adminInfo?.name ?? '';
      const email = adminInfo?.email ?? '';
      replacement = `${name}, ${email}`;
      if (name.length === 0 && email.length > 0) {
        replacement = adminInfo.email;
      }
      if (name.length === 0 && email.length === 0) {
        replacement = '';
      }
      message = message.replace('[[AdminContact]]', replacement);
    } else {
      message = message.replace(' ([[AdminContact]])', '');
    }
    return message;
  }, [content, adminInfo]);

  // Preprocess the markdown to wrap (email) with a mailto link
  const preprocessMarkdown = (text: string) =>
    // Regular expression to find emails inside parentheses, skipping existing markdown links
    text.replace(
      /(?<!\])\(([^)]+@[^\s]+)\)/g,
      (match, email) =>
        // Replace with markdown link syntax including parentheses
        `[${match}](mailto:${email})`,
    );

  useEffect(() => {
    const maxAttempts = 20; // Set a maximum number of attempts to find the <a> tag
    let attempts = 0; // Initialize a counter for attempts

    const intervalId = setInterval(() => {
      // Find the parent container with the class 'wmde-markdown'
      const container = document.querySelector('.wmde-markdown');

      // If the container exists, find the <a> tag within it
      if (container) {
        const anchorTag = container.querySelector('a');

        // If the <a> tag exists, add the custom attribute
        if (anchorTag) {
          anchorTag.setAttribute('data-exclude-lg', 'true');
          clearInterval(intervalId); // Stop the interval once the <a> tag is found and modified
        }
      }

      attempts += 1; // Increment the attempt counter

      // Stop the interval after maxAttempts if the <a> tag is not found
      if (attempts >= maxAttempts) {
        console.warn('Max attempts reached: <a> tag not found');
        clearInterval(intervalId); // Clear the interval
      }
    }, 100); // Retry every 100ms

    // Clean up the interval if the component unmounts
    return () => clearInterval(intervalId);
  }, []);

  return (
    <>
      <div className="d-flex flex-row p-1 p-lg-2">
        <div className="d-flex flex-column col-12 col-md-6 max-content-height">
          <h4 className="d-inline-block mb-3" data-testid="welcome_title">
            {title || defaultWelcomeTitle}
          </h4>
          <div>
            {logoUrl && (
              <img
                className={styles.logoImg}
                src={logoUrl}
                alt="Company logo"
              />
            )}
            <MarkdownViewer content={preprocessMarkdown(welcomeMessageText)} />
          </div>
        </div>
        <div className="d-none d-md-flex col-6 ps-4">
          <EmbedVideo url={videoUrl} transcript={transcript} />
        </div>
      </div>
      <div className="d-flex d-md-none">
        <Button
          variant="primary"
          titleText="Watch Overview Video"
          icon={PlayIcon}
          onClick={() => setShowModal(true)}
        />
      </div>
      {showModal ? (
        <EmbedVideoModal
          videoUrl={videoUrl}
          transcript={transcript}
          onCancel={() => setShowModal(false)}
        />
      ) : null}
    </>
  );
}
